import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import LockResetIcon from '@mui/icons-material/LockReset';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { getColorScheme } from '../../utils/formatUtils';

import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu, MenuItem,
  Paper,
  Select,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import userServiceInstance from '../../services/UserService';
import { formatDate, formatUserName } from '../../utils/formatUtils';
import { format, formatDistanceToNow } from 'date-fns';
import AdminPageWrapper from './AdminPageWrapper';
import { userManagementHelpItems } from '../../utils/helpItems';
import CsvUserImport from './CsvUserImport';

const PROTECTED_USERS = ['admin@srmd.org'];

const UserManagement = ({ darkMode, toggleDarkMode, themeVariant }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Form validation state
  const [errors, setErrors] = useState({});

  // Add this to your state
  const [languages, setLanguages] = useState([
    { name: 'English' },
    { name: 'Hindi' },
    { name: 'Gujarati' },
    { name: 'Spanish' },
    { name: 'French' },
    { name: 'German' },
    { name: 'Kannada' },
    { name: 'Mandarin' },
    { name: 'Portuguese' },
    { name: 'Korean' },
    { name: 'Russian' },
    // Add more languages as needed
  ]);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    userType: 'user',
    languageAccess: {},
    notificationEmail: '' // Add this field
  });

  // Add this state to track selected rows
  const [rowSelection, setRowSelection] = useState({});

  // Add state for tabs
  const [tabValue, setTabValue] = useState(0);
  
  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Add this to your existing state variables
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Add this helper function within your component
  const isProtectedUser = (user) => {
    if (!user) return false;
    return PROTECTED_USERS.includes(user.email);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('authUser'));
      const response = await userServiceInstance.getAllUsers(
        userData.email, 
        userData.access
      );
      setUsers(response);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load users. Please try again later.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Trim the first and last name before validation
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    
    if (!firstName) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!lastName) {
      newErrors.lastName = 'Last name is required';
    }
    
    // For new users, auto-update the form data with trimmed values
    if (!editMode) {
      setFormData(prev => ({
        ...prev,
        firstName: firstName,
        lastName: lastName,
        email: `${firstName.toLowerCase()}_${lastName.toLowerCase()}@vaani.org`
      }));
    }
    
    // No need to validate email as it's auto-generated and read-only
    
    if (!editMode && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (!editMode && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.notificationEmail && 
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.notificationEmail)) {
        newErrors.notificationEmail = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddUser = () => {
    // Initialize default language access (None for all languages)
    const defaultAccess = {};
    languages.forEach(lang => {
      defaultAccess[lang.name] = 'READ';
    });
    
    setFormData({
      firstName: '',
      lastName: '',
      email: '', // Email will be auto-generated as users type first/last name
      notificationEmail: '',
      password: '',
      userType: 'user',
      languageAccess: defaultAccess
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleEditUser = (user) => {
    if (isProtectedUser(user)) {
      setSnackbar({
        open: true,
        message: 'Protected users cannot be modified',
        severity: 'warning'
      });
      return;
    }

    setCurrentUser(user);
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      notificationEmail: user.notificationEmail || '', // Add this line
      email: user.email || '',
      password: '',
      userType: user.userType || 'user',
      languageAccess: user.languageAccess || {} // Initialize language access
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDeleteUser = (user) => {
    if (isProtectedUser(user)) {
      setSnackbar({
        open: true,
        message: 'Protected users cannot be deleted',
        severity: 'warning'
      });
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete user ${user.firstName} ${user.lastName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const userData = JSON.parse(localStorage.getItem('authUser'));
          await userServiceInstance.deleteUser(
            user.email,
            userData.email,
            userData.access
          );
          
          setSnackbar({
            open: true,
            message: 'User deleted successfully!',
            severity: 'success'
          });
          
          fetchUsers();
        } catch (error) {
          console.error('Failed to delete user:', error);
          setSnackbar({
            open: true,
            message: 'Failed to delete user. Please try again.',
            severity: 'error'
          });
        }
      }
    });
  };

  // Add this function to handle batch deletion
  const handleBatchDelete = () => {
    const selectedUsers = Object.keys(rowSelection).map(index => users[parseInt(index)]);
    
    // Filter out protected users from selection
    const deletableUsers = selectedUsers.filter(user => !isProtectedUser(user));
    
    // If protected users were filtered out, show a warning
    if (deletableUsers.length < selectedUsers.length) {
      setSnackbar({
        open: true,
        message: "Protected users were excluded from deletion",
        severity: "info"
      });
      
      // If no deletable users remain, return early
      if (deletableUsers.length === 0) return;
    }
    
    const userCount = deletableUsers.length;
    if (userCount === 0) return;
    
    Swal.fire({
      title: 'Batch Delete Users',
      text: `Are you sure you want to delete ${userCount} selected user${userCount > 1 ? 's' : ''}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const userData = JSON.parse(localStorage.getItem('authUser'));
          const currentUserEmail = userData.email;
          
          // Filter out the current user if they're in the selection
          const usersToDelete = deletableUsers.filter(user => user.email !== currentUserEmail);
          
          if (usersToDelete.length === 0) {
            setSnackbar({
              open: true,
              message: 'Cannot delete your own account',
              severity: 'warning'
            });
            setLoading(false);
            return;
          }
          
          // Process deletions sequentially
          let successCount = 0;
          let errorCount = 0;
          
          for (const user of usersToDelete) {
            try {
              await userServiceInstance.deleteUser(
                user.email,
                userData.email,
                userData.access
              );
              successCount++;
            } catch (error) {
              console.error(`Failed to delete user ${user.email}:`, error);
              errorCount++;
            }
          }
          
          // Clear row selection
          setRowSelection({});
          
          // Show results
          if (successCount > 0) {
            setSnackbar({
              open: true,
              message: `Successfully deleted ${successCount} user${successCount !== 1 ? 's' : ''}${errorCount > 0 ? ` (${errorCount} failed)` : ''}`,
              severity: errorCount > 0 ? 'warning' : 'success'
            });
            
            // Refresh the user list
            fetchUsers();
          } else {
            setSnackbar({
              open: true,
              message: 'Failed to delete users',
              severity: 'error'
            });
          }
        } catch (error) {
          console.error('Batch delete error:', error);
          setSnackbar({
            open: true,
            message: 'An error occurred during batch deletion',
            severity: 'error'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setErrors({});
  };

  // Update the handleFormChange function to generate email automatically

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Update the form data with the new value
    setFormData(prevData => {
      const newData = {
        ...prevData,
        [name]: value
      };
      
      // If not in edit mode and either first name or last name changes, auto-generate the email
      if (!editMode && (name === 'firstName' || name === 'lastName')) {
        // Get the current first and last names, using the updated value for the field that changed
        const firstName = name === 'firstName' ? value : prevData.firstName;
        const lastName = name === 'lastName' ? value : prevData.lastName;
        
        // Only generate email if both first and last name have values
        if (firstName.trim() && lastName.trim()) {
          // Create email as first_last@vaani.org using lowercase trimmed values
          const generatedEmail = `${firstName.trim().toLowerCase()}_${lastName.trim().toLowerCase()}@vaani.org`;
          newData.email = generatedEmail;
        }
      }
      
      return newData;
    });
  };

  const handleLanguageAccessChange = (langCode, access) => {
    setFormData(prev => ({
      ...prev,
      languageAccess: {
        ...prev.languageAccess,
        [langCode]: access
      }
    }));
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem('authUser'));
      
      // Add current user's email to the form data for tracking
      const updatedFormData = {
        ...formData,
        updatedBy: userData.email
      };
      
      // For new users, set the createdBy field
      if (!editMode) {
        updatedFormData.createdBy = userData.email;
        updatedFormData.updatedBy = userData.email;
      }
      
      if (editMode && currentUser) {
        // Update existing user
        await userServiceInstance.updateUser(
          currentUser.email,
          updatedFormData,
          userData.email,
          userData.access
        );
        
        setSnackbar({
          open: true,
          message: 'User updated successfully!',
          severity: 'success'
        });
      } else {
        // Create new user
        await userServiceInstance.register(
          updatedFormData.firstName,
          updatedFormData.lastName,
          updatedFormData.email,
          updatedFormData.password,
          updatedFormData.userType,
          updatedFormData.createdBy
        );
        
        setSnackbar({
          open: true,
          message: 'User created successfully!',
          severity: 'success'
        });
      }
      
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Form submission error:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${editMode ? 'update' : 'create'} user. Please try again.`,
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Update handleResetPassword to protect admin users
  const handleResetPassword = (user) => {
    if (isProtectedUser(user)) {
      setSnackbar({
        open: true,
        message: 'Protected users cannot have their password reset',
        severity: 'warning'
      });
      return;
    }

    Swal.fire({
      title: 'Reset Password',
      text: `Reset password for ${user.firstName} ${user.lastName} to Bapa0926?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reset it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const userData = JSON.parse(localStorage.getItem('authUser'));
          
          // Create a reset password object including updatedBy field
          const resetData = {
            password: 'Bapa0926',
            updatedBy: userData.email // Add this line
          };
          
          // Use the updateUser method to update just the password
          await userServiceInstance.updateUser(
            user.email,
            resetData,
            userData.email,
            userData.access
          );
          
          setSnackbar({
            open: true,
            message: 'Password reset successfully to Bapa0926!',
            severity: 'success'
          });
        } catch (error) {
          console.error('Failed to reset password:', error);
          setSnackbar({
            open: true,
            message: 'Failed to reset password. Please try again.',
            severity: 'error'
          });
        }
      }
    });
  };

  // Replace the columns array with this optimized version
  const columns = [
    {
      accessorKey: 'firstName',
      header: 'First Name',
      size: 120, // Reduced from 150
    },
    {
      accessorKey: 'lastName',
      header: 'Last Name',
      size: 120, // Reduced from 150
    },
    {
      accessorKey: 'email',
      header: 'Email',
      size: 180, // Reduced from 200
      Cell: ({ cell, row }) => {
        const user = row.original;
        const isProtected = isProtectedUser(user);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography 
              variant="body2"
              sx={{ 
                fontWeight: isProtected ? 'medium' : 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {cell.getValue()}
            </Typography>
            {isProtected && (
              <Chip 
                label="Protected" 
                sx={{
                    color: 'secondary.main',
                    ml: 0.5
                }}
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        );
      }
    },
    {
      accessorKey: 'userType',
      header: 'Role',
      size: 80, // Reduced from 120
      Cell: ({ cell }) => (
        <Typography 
          variant="body2" 
          sx={{
            textTransform: 'capitalize',
            fontWeight: cell.getValue() === 'admin' ? 'bold' : 'normal',
            color: cell.getValue() === 'admin' ? 'primary.main' : 'text.primary'
          }}
        >
          {cell.getValue()}
        </Typography>
      )
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      size: 120, // Reduced from 180
      Cell: ({ cell }) => {
        return formatDate(cell.getValue(), 'table');
      }
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      size: 120, // Reduced from 180
      Cell: ({ cell }) => {
        return formatDate(cell.getValue(), 'table');
      }
    },
    {
      accessorKey: 'createdBy',
      header: 'Created By',
      size: 120, // Reduced from 150
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? (
          <Typography variant="body2" noWrap>
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            System
          </Typography>
        );
      }
    },
    {
      accessorKey: 'updatedBy',
      header: 'Updated By',
      size: 120, // Reduced from 150
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? (
          <Typography variant="body2" noWrap>
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            System
          </Typography>
        );
      }
    },
    // Optimize language access column
    {
      accessorKey: 'languageAccess',
      header: 'Languages',
      size: 150, // Reduced from 200
      Cell: ({ cell, row }) => {
        const accessData = cell.getValue() || {};
        const entries = Object.entries(accessData).filter(([_, access]) => access !== 'None');
        const displayEntries = entries.slice(0, 2); // Show only 2 instead of 3
        const remaining = Math.max(0, entries.length - 2);
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {displayEntries.map(([lang, access]) => {
              return (
                <Chip 
                  key={lang}
                  label={`${lang.substring(0, 3)}: ${access.substring(0, 1)}`} // Shorter labels
                  size="small"
                  sx={{ 
                    mr: 0.5,
                    backgroundColor: getColorScheme(access).bg,
                    color: getColorScheme(access).color,
                    border: '1px solid',
                    borderColor: getColorScheme(access).border,
                    '& .MuiChip-label': { px: 1, fontSize: '0.65rem' }
                  }}
                />
              );
            })}
            {remaining > 0 && (
              <Chip
                label={`+${remaining}`}
                size="small"
                variant="outlined"
                onClick={() => handleLanguageDialogOpen(row.original)}
                sx={{ cursor: 'pointer', '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }}
              />
            )}
          </Box>
        );
      }
    },
    // Add column for last login time
    {
      accessorKey: 'lastLoginAt',
      header: 'Last Login',
      size: 150,
      Cell: ({ cell }) => {
        const date = cell.getValue();
        if (!date) return <Typography variant="body2" color="text.secondary">Never</Typography>;
        
        return (
          <Tooltip title={format(new Date(date), 'PPpp')}>
            <Typography variant="body2">
              {formatDistanceToNow(new Date(date), { addSuffix: true })}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      accessorKey: 'notificationEmail',
      header: 'Notification Email',
      size: 200,
      Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
              <Tooltip title={value || "Using primary email"}>
                  <Typography 
                      variant="body2" 
                      sx={{ 
                          fontStyle: value ? 'normal' : 'italic',
                          color: value ? 'text.primary' : 'text.secondary'
                      }}
                  >
                      {value || 'Primary Email'}
                  </Typography>
              </Tooltip>
          );
      }
    },
    // Make actions column smaller
    {
      id: 'actions',
      header: 'Actions',
      size: 60, // Reduced from 80
      Cell: ({ row }) => {
        const user = row.original;
        
        return (
          <Tooltip title="User Actions">
            <IconButton 
              onClick={(e) => handleMenuOpen(e, user)}
              size="small"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  // Add these state variables for the action menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [selectedUserLanguages, setSelectedUserLanguages] = useState({});

  // Handle menu opening
  const handleMenuOpen = (event, user) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  // Handle menu closing
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedUser(null);
  };

  // Handle language dialog opening
  const handleLanguageDialogOpen = (user) => {
    setSelectedUserLanguages(user.languageAccess || {});
    setLanguageDialogOpen(true);
  };

  // Handle language dialog closing
  const handleLanguageDialogClose = () => {
    setLanguageDialogOpen(false);
  };

  const renderLanguageAccessFields = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Language Access Permissions
      </Typography>
      <Grid container spacing={2}>
        {languages.map(lang => (
          <Grid item xs={12} sm={6} key={lang.name}>
            <FormControl fullWidth size="small">
              <InputLabel id={`lang-access-${lang.name}`}>{lang.name}</InputLabel>
              <Select
                labelId={`lang-access-${lang.name}`}
                value={formData.languageAccess[lang.name] || 'READ'}
                onChange={(e) => handleLanguageAccessChange(lang.name, e.target.value)}
                label={lang.name}
              >
                
                <MenuItem value="READ">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ width: 12, height: 12, borderRadius: 6, backgroundColor: 'rgba(46, 125, 50, 0.15)', mr: 1 }}></Box>
                    READ
                  </Box>
                </MenuItem>
                <MenuItem value="WRITE">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ width: 12, height: 12, borderRadius: 6, backgroundColor: 'rgba(25, 118, 210, 0.15)', mr: 1 }}></Box>
                    WRITE
                  </Box>
                </MenuItem>
                <MenuItem value="ADMIN">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ width: 12, height: 12, borderRadius: 6, backgroundColor: 'rgba(211, 47, 47, 0.15)', mr: 1 }}></Box>
                    ADMIN
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Add this refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchUsers();
      setSnackbar({
        open: true,
        message: 'User list refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      // Error is already handled in fetchUsers
    } finally {
      setRefreshing(false);
    }
  };
  
  return (
    <AdminPageWrapper 
      title="User Management"
      helpItems={userManagementHelpItems}
      darkMode={darkMode}
      toggleDarkMode={toggleDarkMode}
      themeVariant={themeVariant}
    >
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        centered
      >
        <Tab label="User List" />
        <Tab label="Import Users" />
      </Tabs>
      
      {/* Tab content */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          className="user-management-header" // Add class for help targeting
        >
          <Typography variant="h5" component="h1">
            User Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh user list">
              <IconButton 
                color="primary"
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="user-refresh-button"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              className="user-create-button" // Add class for help targeting
            >
              Add New User
            </Button>
          </Box>
        </Box>

        {/* Add className to table for help targeting */}
        <Paper elevation={2} className="user-list">
          <MaterialReactTable
            columns={columns}
            data={users}
            state={{ 
              isLoading: loading,
              rowSelection
            }}
            enableRowSelection
            onRowSelectionChange={setRowSelection}
            positionToolbarAlertBanner="bottom"
            // Add these properties for better responsiveness
            layoutMode="grid" // Uses CSS grid layout for better responsiveness
            enableColumnResizing={true} // Let users resize columns
            enablePinning={true} // Allow column pinning
            // Improve density settings
            muiTableProps={{
              sx: {
                tableLayout: 'fixed', // Makes the table respect column widths better
              },
            }}
            // Set default display column settings
            initialState={{
              density: 'compact', // Use compact density by default
              columnVisibility: {
                createdBy: false, // Hide less important columns by default
                createdAt: false,
              },
            }}
          />
        </Paper>

        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {editMode ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <DialogContent>
            <Box component="form" noValidate sx={{ mt: 2 }}>
              <TextField
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleFormChange}
                fullWidth
                margin="normal"
                error={!!errors.firstName}
                helperText={errors.firstName || "First name will be used to generate email"}
                inputProps={{
                  onBlur: (e) => {
                    // Trim whitespace on blur
                    const trimmed = e.target.value.trim();
                    if (trimmed !== e.target.value) {
                      setFormData(prev => ({
                        ...prev,
                        firstName: trimmed
                      }));
                    }
                  }
                }}
              />
              <TextField
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleFormChange}
                fullWidth
                margin="normal"
                error={!!errors.lastName}
                helperText={errors.lastName || "Last name will be used to generate email"}
                inputProps={{
                  onBlur: (e) => {
                    // Trim whitespace on blur
                    const trimmed = e.target.value.trim();
                    if (trimmed !== e.target.value) {
                      setFormData(prev => ({
                        ...prev,
                        lastName: trimmed
                      }));
                    }
                  }
                }}
              />
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email || (editMode ? "" : "Auto-generated from first and last name")}
                disabled={true} // Always disabled, both in new user and edit mode
                InputProps={{
                  readOnly: true,
                  sx: { 
                    bgcolor: 'action.disabledBackground',
                    '& .MuiInputBase-input': {
                      WebkitTextFillColor: 'text.disabled',
                    }
                  }
                }}
              />
              {!editMode && (
                <TextField
                  name="password"
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password}
                />
              )}
              <FormControl fullWidth margin="normal">
                <InputLabel id="user-type-label">User Type</InputLabel>
                <Select
                  labelId="user-type-label"
                  name="userType"
                  value={formData.userType}
                  onChange={handleFormChange}
                  label="User Type"
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>

              <TextField
                name="notificationEmail"
                label="Notification Email (Optional)"
                type="email"
                value={formData.notificationEmail || ''}
                onChange={handleFormChange}
                fullWidth
                margin="normal"
                error={!!errors.notificationEmail}
                helperText={errors.notificationEmail || "Leave blank to use primary email for notifications"}
              />
              
              {/* Add the language access fields */}
              {renderLanguageAccessFields()}
              
              
            </Box>
            <Box sx={{ mt: 3 }}>
              {editMode && currentUser && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Created By: {currentUser.createdBy || 'System'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Last Updated By: {formatUserName(currentUser.updatedBy) || 'System'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button 
              onClick={handleFormSubmit} 
              variant="contained" 
              color="primary"
            >
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Action Menu */}
        <Menu
          id="user-actions-menu"
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {selectedUser && isProtectedUser(selectedUser) && (
            <MenuItem disabled>
              <Chip 
                label="Protected" 
                sx={{
                    color: 'secondary.main',
                    ml: 0.5
                }}
                variant="outlined"
                size="small"
              />
            </MenuItem>
          )}
          <MenuItem 
            onClick={() => {
              handleEditUser(selectedUser);
              handleMenuClose();
            }}
            disabled={selectedUser && isProtectedUser(selectedUser)}
          >
            <EditIcon fontSize="small" color={selectedUser && isProtectedUser(selectedUser) ? "disabled" : "primary"} sx={{ mr: 1 }} />
            Edit User
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleResetPassword(selectedUser);
              handleMenuClose();
            }}
            disabled={selectedUser && isProtectedUser(selectedUser)}
          >
            <LockResetIcon fontSize="small" color={selectedUser && isProtectedUser(selectedUser) ? "disabled" : "secondary"} sx={{ mr: 1 }} />
            Reset Password
          </MenuItem>
          
          <MenuItem 
            onClick={() => {
              handleDeleteUser(selectedUser);
              handleMenuClose();
            }}
            sx={{ color: selectedUser && (isProtectedUser(selectedUser) || selectedUser.email === JSON.parse(localStorage.getItem('authUser')).email) ? 'text.disabled' : 'error.main' }}
            disabled={selectedUser && (isProtectedUser(selectedUser) || selectedUser.email === JSON.parse(localStorage.getItem('authUser')).email)}
          >
            <DeleteIcon 
              fontSize="small" 
              color={selectedUser && (isProtectedUser(selectedUser) || selectedUser.email === JSON.parse(localStorage.getItem('authUser')).email) ? "disabled" : "error"} 
              sx={{ mr: 1 }} 
            />
            Delete User
          </MenuItem>
        </Menu>

        {/* Language Access Dialog */}
        <Dialog 
          open={languageDialogOpen} 
          onClose={handleLanguageDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedUser && (
              <>Language Permissions for {selectedUser.firstName} {selectedUser.lastName}</>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {Object.entries(selectedUserLanguages).map(([lang, access]) => {
                return (
                  <Grid item xs={6} sm={4} key={lang}>
                    <Chip 
                      label={`${lang}: ${access}`}
                      sx={{ 
                        mr: 0.5,
                        backgroundColor: getColorScheme(access).bg,
                        color: getColorScheme(access).color,
                        border: '1px solid',
                        borderColor: getColorScheme(access).border,
                        '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLanguageDialogClose}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <CsvUserImport />
      </TabPanel>
    </AdminPageWrapper>
  );
};

// TabPanel helper component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-management-tabpanel-${index}`}
      aria-labelledby={`user-management-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

export default UserManagement;